import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Trash2 } from 'lucide-react'
import { Button } from '~/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { PageHeader } from '~/components/PageHeader'
import { useCurrentUser } from '~/lib/current-user'
import { listRoles } from '~/server/roles'
import { deleteUser, listUsers, updateUserRole } from '~/server/users'
import { seo } from '~/utils/seo'
import type { Role, User } from '@rapid-response/shared'

export const Route = createFileRoute('/admin/users')({
  head: () => ({ meta: seo({ title: 'Admin — Users' }) }),
  component: AdminUsersPage,
})

function AdminUsersPage() {
  const { user: currentUser } = useCurrentUser()
  const [users, setUsers] = useState<User[] | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [pendingId, setPendingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [u, r] = await Promise.all([listUsers(), listRoles()])
      setUsers(u)
      setRoles(r)
    } catch (e) {
      toast.error((e as Error).message)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function handleRole(userId: string, roleId: string) {
    setPendingId(userId)
    try {
      await updateUserRole({ data: { userId, roleId } })
      await load()
      toast.success('Role updated')
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPendingId(null)
    }
  }

  async function handleDelete(userId: string) {
    setPendingId(userId)
    try {
      await deleteUser({ data: { userId } })
      await load()
      toast.success('User deleted')
    } catch (e) {
      toast.error((e as Error).message)
    } finally {
      setPendingId(null)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <PageHeader title="Users" description="Assign roles and remove users." />

      {users === null ? (
        <div className="text-muted-foreground text-sm">Loading...</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-2 font-medium">Name</th>
              <th className="py-2 font-medium">Email</th>
              <th className="py-2 font-medium">Role</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id
              return (
                <tr key={u.id} className="border-t">
                  <td className="py-2">{u.name}</td>
                  <td className="py-2 text-muted-foreground">{u.email}</td>
                  <td className="py-2">
                    <Select
                      value={u.role.id}
                      onValueChange={(roleId) => handleRole(u.id, roleId)}
                      disabled={pendingId === u.id}
                    >
                      <SelectTrigger className="h-8 w-44">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(u.id)}
                      disabled={pendingId === u.id || isSelf}
                      title={isSelf ? 'You cannot delete your own account' : 'Delete user'}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
